// =============================================================
// folderDrop.js — read a dropped FOLDER, not just the files in it.
//
// Part of the removable DAM module, and deliberately import-free: it is used by
// both the authenticated upload modal and the PUBLIC share portal, and the
// public bundle must never reach into app code (it would drag in MSAL).
// frontend/src/utils/docFolders.js does the same walk for deal documents — it
// is not shared here for that reason, and because the DAM's only touch points
// outside its own folder are the handful marked in DAM_MODULE.md.
//
// WHY A WALK AT ALL. A `<input webkitdirectory>` pick populates each File's
// `webkitRelativePath`, so a directory chosen through the file picker already
// carries its structure. A DRAG-DROPPED folder does not: `dataTransfer.files`
// is a flat list with empty `webkitRelativePath`, and on most browsers it does
// not include the folder's contents at all — the only way to reach them is the
// File System Entry API on `dataTransfer.items`. Without this, dropping a
// folder either uploaded nothing or uploaded a flattened pile.
// =============================================================

// Cap the walk. A dropped tree is arbitrary — somebody's whole Pictures library,
// or a symlink loop presenting as infinite depth — and a drop that hangs the tab
// is worse than one that says it took the first N files.
const MAX_FILES = 2000;
const MAX_DEPTH = 12;

// Attach the path we discovered to the File. `webkitRelativePath` is read-only
// on a File, so the walk writes its own property and publicApi/damApi read
// whichever is populated.
function tag(file, path) {
  try { file.harvusRelativePath = path; } catch { /* frozen File — the flat name still uploads */ }
  return file;
}

function readEntries(reader) {
  // readEntries() returns AT MOST 100 entries per call and signals the end with
  // an empty batch — a single call silently truncates a folder of 101 files.
  return new Promise((resolve) => reader.readEntries(resolve, () => resolve([])));
}

async function walkEntry(entry, prefix, out, depth) {
  if (!entry || out.length >= MAX_FILES) return;
  const path = prefix ? `${prefix}/${entry.name}` : entry.name;

  if (entry.isFile) {
    const file = await new Promise((resolve) => entry.file(resolve, () => resolve(null)));
    if (file) out.push(tag(file, path));
    return;
  }
  if (entry.isDirectory && depth < MAX_DEPTH) {
    const reader = entry.createReader();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const batch = await readEntries(reader);
      if (!batch.length) break;
      for (const child of batch) {
        await walkEntry(child, path, out, depth + 1);
        if (out.length >= MAX_FILES) return;
      }
    }
  }
}

// Collect every file from a drop, keeping the folder structure where the browser
// exposes it. Falls back to the flat `dataTransfer.files` when the Entry API is
// unavailable — a browser without it should still be able to drop files, just
// without their folders.
export async function filesFromDrop(dataTransfer) {
  const items = dataTransfer && dataTransfer.items ? Array.from(dataTransfer.items) : [];
  const entries = items
    .map(it => (typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null))
    .filter(Boolean);

  if (!entries.length) return Array.from((dataTransfer && dataTransfer.files) || []);

  const out = [];
  for (const entry of entries) {
    // eslint-disable-next-line no-await-in-loop
    await walkEntry(entry, '', out, 0);
    if (out.length >= MAX_FILES) break;
  }
  // An Entry API that yielded nothing (a permissions quirk, a synthetic drop)
  // must not turn a working drop into an empty one.
  return out.length ? out : Array.from((dataTransfer && dataTransfer.files) || []);
}

// The folder a File came in with, from either source. Display only — the server
// normalizes what it stores (api/src/damIntake.js).
export function folderOfFile(file) {
  const rel = (file && (file.webkitRelativePath || file.harvusRelativePath)) || '';
  const cut = rel.lastIndexOf('/');
  return cut > 0 ? rel.slice(0, cut) : '';
}
